import { useCallback, useId, useRef } from "react";
import { createPortal } from "react-dom";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseIcon from "@mui/icons-material/Close";

import { BREAKPOINTS } from "../../../utils/constants";
import { useResponsive } from "../../../hooks/useResponsive";
import { useEscapeKey, useModalA11y } from "../../../hooks";
import { IconButton } from "../../IconButton";
import { DiscardPopUpProps } from "./types";
import {
  IconBadge,
  Title,
  Description,
  DialogBackdrop,
  DialogBody,
  DialogHeader,
  HeadingBlock,
  DialogFooter,
  SheetBackdrop,
  SheetGroup,
  SheetCard,
  SheetBody,
  SheetActionButton,
  SheetCancelButton,
  Dialog,
  Eyebrow,
  CloseSlot,
} from "./styles";
import { CONFIRM_BUTTON_TYPE, ConfirmDetails } from "../confirmShared";
import Button, { ButtonTypes } from "../../Button";

const modalElement = document.getElementById("modal-root") as HTMLElement;

export const DiscardPopUp: React.FC<DiscardPopUpProps> = ({
  portalContainer,
  handleOnDiscard,
  isOpen,
  setIsOpen,
  title,
  text,
  eyebrow,
  details,
  tone = "warning",
  icon,
  confirmLabel = "Discard changes",
  cancelLabel = "Keep editing",
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const describedBy = text ? descriptionId : undefined;

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  useEscapeKey(handleClose, isOpen);
  useModalA11y(isOpen, containerRef);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  // Handle Escape before the containing MUI drawer consumes the event.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      handleClose();
    }
  };

  const badge = (
    <IconBadge $tone={tone}>{icon ?? <WarningAmberRoundedIcon />}</IconBadge>
  );

  const detailsPanel = details && <ConfirmDetails detail={details} />;

  const renderMobile = () => (
    <SheetBackdrop onClick={handleBackdropClick}>
      <SheetGroup
        ref={containerRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
      >
        <SheetCard>
          <SheetBody>
            {badge}
            <Title id={titleId}>{title}</Title>
            <Description id={descriptionId}>{text}</Description>
            {detailsPanel}
          </SheetBody>
          <SheetActionButton
            $tone={tone}
            onClick={handleOnDiscard}
            aria-label={confirmLabel}
          >
            {confirmLabel}
          </SheetActionButton>
        </SheetCard>
        <SheetCancelButton onClick={handleClose} aria-label={cancelLabel}>
          {cancelLabel}
        </SheetCancelButton>
      </SheetGroup>
    </SheetBackdrop>
  );

  const renderDesktop = () => (
    <DialogBackdrop onClick={handleBackdropClick}>
      <Dialog
        ref={containerRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
      >
        <CloseSlot>
          <IconButton onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </CloseSlot>
        <DialogBody>
          <DialogHeader>
            {badge}
            <HeadingBlock>
              {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
              <Title id={titleId}>{title}</Title>
              <Description id={descriptionId}>{text}</Description>
            </HeadingBlock>
          </DialogHeader>
          {detailsPanel}
        </DialogBody>
        <DialogFooter>
          <Button
            type={CONFIRM_BUTTON_TYPE[tone]}
            onClick={handleOnDiscard}
            aria-label={confirmLabel}
          >
            {confirmLabel}
          </Button>
          <Button
            type={ButtonTypes.cancel}
            onClick={handleClose}
            aria-label={cancelLabel}
          >
            {cancelLabel}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogBackdrop>
  );

  const modalContent = isCompact ? renderMobile() : renderDesktop();

  return createPortal(
    isOpen ? modalContent : null,
    portalContainer ?? modalElement,
  );
};
